/**
 * CompactMergeUnsupportedNotice — compact-popover twin of the full-canvas
 * `MergeCapabilityNotice` amber banner (EDG-01/03), for a MERGE statement on
 * an engine that has no MERGE statement at all (MySQL, SQLite). Rendered
 * INSTEAD of the generic `CompactDegradeNotice` for this one case: the
 * generic notice's headline ("MERGE statements are edited in the full
 * editor.") + a single "Open full editor" button would route the user
 * DEEPER into editing a statement the connected engine can never run. This
 * notice states the dialect truth instead and makes the PRIMARY action the
 * real fix — convert the MERGE in place into the engine's native upsert
 * (`ON DUPLICATE KEY UPDATE` / `ON CONFLICT DO UPDATE`), so the VQE
 * re-parses the buffer into the first-class upsert editor. "Edit as SQL" /
 * "Open full editor" stay as the fallback, never the primary.
 *
 * REUSES (does not duplicate) the settled §5 transform/applier —
 * `convertMergeToUpsert` (pure, decides convertibility + emits the SQL) and
 * `applyMergeConvert` (thin whole-statement range-replace committer with a
 * stale-buffer guard). This component only decides what to render for the
 * two outcomes and wires the click; it is intentionally self-contained
 * (unlike `MergeRenderer`/`MergeCapabilityNotice`'s split), since the compact
 * wiring conditional in `CompactVqeContent` only needs to hand it the raw
 * outline/bufferText/statementRange/editContext.
 *
 * Every copy string is reused from `vqeCopy.capability.merge.*` — no new
 * strings were needed; `notConvertible` already has a generic fallback line
 * for any reason it doesn't name explicitly.
 *
 * spec: .superpowers/sdd/vqe-polish/wave-1/task-compact-merge-brief.md.
 */

import { AlertTriangle, ExternalLink, Wand2 } from 'lucide-react';
import { useCallback, useEffect, useRef } from 'react';

import { Button } from '../../../../../components/ui/button';
import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import type { ParseOptions, SqlEngine, SqlRange } from '../../../../sql-grammar/model';
import { applyMergeConvert } from '../../edit/edits/applyMergeConvert';
import { convertMergeToUpsert } from '../../edit/edits/convertMergeToUpsert';
import type { VqeEditContext } from '../../edit/editContext';
import { ENGINE_DISPLAY_NAME } from '../../dialect/statementCapabilities';
import type { MergeOutline } from '../../render/merge/extractMergeOutline';
import { vqeCopy } from '../../ux/vqeCopy';

export interface CompactMergeUnsupportedNoticeProps {
  readonly engine: SqlEngine;
  /** MERGE outline of the cursor statement (`extractMergeOutline` over `bufferText`/`statementRange`). */
  readonly outline: MergeOutline;
  /** Full live buffer text (`statementRange` indexes into this). */
  readonly bufferText: string;
  /** The MERGE statement's range in `bufferText`. */
  readonly statementRange: SqlRange;
  readonly editContext: VqeEditContext;
  readonly options: ParseOptions;
  /** Fallback: open the full Visual Query Editor workspace seeded with this statement. */
  readonly onOpenFull: () => void;
  /** Fallback: close the popover and focus the same buffer in the SQL editor. */
  readonly onEditAsSql?: () => void;
}

export function CompactMergeUnsupportedNotice({
  engine,
  outline,
  bufferText,
  statementRange,
  editContext,
  options,
  onOpenFull,
  onEditAsSql,
}: CompactMergeUnsupportedNoticeProps) {
  const engineName = ENGINE_DISPLAY_NAME[engine];
  const { track } = useAnalytics();
  const convert = convertMergeToUpsert(outline, bufferText, engine, options);

  const handleConvert = useCallback(async () => {
    if (!convert.ok) return;
    try {
      await applyMergeConvert({
        editContext,
        statementRange,
        originalSource: bufferText,
        generatedSql: convert.sql,
      });
      track('feature:vqe_merge_convert', { engine, outcome: 'applied', surface: 'compact' });
    } catch {
      // Apply failed (e.g. the buffer moved) — no success analytics; the
      // Edit-as-SQL / Open-full-editor fallbacks stay available.
    }
    // `convert` is recomputed every render off `outline`/`bufferText`/`engine`/
    // `options`, so it is safe to close over here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convert, editContext, statementRange, bufferText, engine, track]);

  // Telemetry for encountering a MERGE we can't auto-convert (fires once per
  // mount, low-cardinality — no SQL text).
  const notConvertibleReason = !convert.ok ? convert.reason : null;
  const firedRef = useRef(false);
  useEffect(() => {
    if (notConvertibleReason && !firedRef.current) {
      firedRef.current = true;
      track('feature:vqe_merge_convert', { engine, outcome: 'not-convertible', surface: 'compact' });
    }
  }, [notConvertibleReason, engine, track]);

  const editAsSqlButton = onEditAsSql ? (
    <button
      type="button"
      onClick={onEditAsSql}
      data-testid="vqe-compact-merge-edit-as-sql"
      className="shrink-0 font-medium underline-offset-2 hover:underline"
    >
      {vqeCopy.actions.editAsSql}
    </button>
  ) : null;

  return (
    <div
      role="alert"
      data-testid="vqe-compact-merge-unsupported"
      data-engine={engine}
      className="flex flex-col gap-3 rounded-md border border-amber-300/60 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-200"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
        <span data-testid="vqe-compact-merge-message">{vqeCopy.capability.merge.unsupportedBanner(engineName)}</span>
      </div>
      {!convert.ok ? (
        <p
          className="pl-[22px] text-amber-700 dark:text-amber-300"
          data-testid="vqe-compact-merge-convert-unavailable"
        >
          {vqeCopy.capability.merge.notConvertible(convert.reason)}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-2 pl-[22px]">
        {convert.ok ? (
          <Button
            type="button"
            size="sm"
            onClick={() => void handleConvert()}
            data-testid="vqe-compact-merge-convert-cta"
            className="h-7 gap-1 px-3 text-xs"
          >
            <Wand2 className="h-3.5 w-3.5" aria-hidden />
            {vqeCopy.capability.merge.convertCta}
          </Button>
        ) : null}
        {editAsSqlButton}
        <Button
          type="button"
          size="sm"
          variant={convert.ok ? 'outline' : 'default'}
          onClick={onOpenFull}
          data-testid="vqe-compact-merge-open-full"
          className="h-7 gap-1 px-3 text-xs"
        >
          <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          {vqeCopy.actions.openFullEditor}
        </Button>
      </div>
      {engine === 'mysql' && convert.ok ? (
        <p className="pl-[22px] text-[11px] text-amber-700 dark:text-amber-300" data-testid="vqe-compact-merge-convert-mysql-caveat">
          {vqeCopy.capability.merge.convertMysqlKeyCaveat}
        </p>
      ) : null}
    </div>
  );
}
